/**
 * Zone 2: Recent Activity (What Led Here)
 *
 * Time-windowed event feed — NOT full history.
 * Shows only the last 30–120 minutes of relevant activity.
 * Highlights anomalies and delays.
 */

import { useMemo } from 'react';
import type { TimelineEvent } from '../../services/entityEventTypes';
import type { Anomaly } from '../../services/investigationEngine';

interface RecentActivityProps {
  events: readonly TimelineEvent[];
  anomalies: readonly Anomaly[];
}

const EVENT_ICON: Record<string, string> = {
  STAY_RESERVED: '📅', STAY_CHECKED_IN: '🔑', STAY_CHECKED_OUT: '🚪', STAY_CANCELLED: '❌',
  REQUEST_CREATED: '📝', REQUEST_ASSIGNED: '👤', REQUEST_ESCALATED: '⚠️',
  REQUEST_COMPLETED: '✅', REQUEST_CANCELLED: '❌',
  ORDER_PLACED: '🍽️', ORDER_CONFIRMED: '✓', ORDER_FULFILLED: '✅', ORDER_CANCELLED: '❌',
  HK_TASK_CREATED: '🧹', HK_TASK_STARTED: '🧽', HK_TASK_COMPLETED: '✨',
  HK_TASK_SKIPPED: '⏭️', HK_TASK_BLOCKED: '🚫',
  MAINT_REPORTED: '🔧', MAINT_ASSIGNED: '👷', MAINT_RESOLVED: '✅',
  ECO_OPT_IN: '🌿', ECO_OPT_OUT: '🍂',
  MESSAGE_SENT: '💬',
};

function formatTimeAgo(timestamp: string): string {
  const diff = Date.now() - new Date(timestamp).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ${mins % 60}m ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function formatLabel(event: TimelineEvent): string {
  const base = (event.eventType ?? event.type ?? '').replace(/_/g, ' ').toLowerCase();
  // Capitalize first letter
  return base.charAt(0).toUpperCase() + base.slice(1);
}

export function RecentActivity({ events, anomalies }: RecentActivityProps) {
  const anomalyIds = useMemo(() => new Set(anomalies.map((a) => a.id.replace('anomaly-', ''))), [anomalies]);

  // Show most recent first, limited
  const displayed = [...events].reverse().slice(0, 15);

  if (displayed.length === 0) {
    return (
      <div className="bg-slate-800 border border-slate-700 rounded-lg px-5 py-6 text-center">
        <p className="text-sm text-slate-500">No recent activity in time window</p>
      </div>
    );
  }

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
      <div className="px-5 py-3 border-b border-slate-700/50">
        <h3 className="text-xs font-semibold text-slate-300 uppercase tracking-wider">
          What led here
        </h3>
        <p className="text-[10px] text-slate-500 mt-0.5">
          Last {displayed.length} events in recent window
        </p>
      </div>

      <div className="divide-y divide-slate-700/30">
        {displayed.map((event) => {
          const isAnomaly = anomalyIds.has(event.id);
          return (
            <div
              key={event.id}
              className={`px-5 py-2.5 flex items-start gap-3 ${
                isAnomaly ? 'bg-amber-950/20 border-l-2 border-l-amber-500' : ''
              }`}
            >
              <span className="text-sm mt-0.5 flex-shrink-0">
                {EVENT_ICON[event.eventType ?? ''] ?? '•'}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-slate-200 truncate">
                    {formatLabel(event)}
                  </span>
                  {isAnomaly && (
                    <span className="text-[9px] bg-amber-900/50 text-amber-300 px-1.5 py-0.5 rounded font-medium">
                      anomaly
                    </span>
                  )}
                </div>
                {(event.roomNumber || event.guestName) && (
                  <p className="text-[10px] text-slate-500 mt-0.5 truncate">
                    {[event.roomNumber && `Room ${event.roomNumber}`, event.guestName]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                )}
              </div>
              <span className="text-[10px] text-slate-500 flex-shrink-0 mt-0.5">
                {formatTimeAgo(event.timestamp)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
